import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { distinctUntilChanged, first, skip, takeUntil } from 'rxjs/operators';
import swal from 'sweetalert2';
import { AuthService } from '../../../auth/AuthService';
import { AuditSeverityTag, QuestionsService, severityLabel } from '../../../Services/questions.service';

@Component({
  selector: 'pm-addquestion',
  templateUrl: './addquestion.component.html',
  styleUrls: ['./addquestion.component.css']
})
export class AddquestionComponent implements OnInit, OnDestroy {
  addQuestionsForm: FormGroup;
  questionId: string;
  selectedDomain: 'DENTAL' | 'MEDICAL' = 'DENTAL';
  isCustomCategory = false;
  categoryArray: any[] = [];
  severityTags: AuditSeverityTag[] = [];
  severityOptions: AuditSeverityTag[] = [];
  severityLoading = false;
  severityError = '';
  loadingQuestion = false;
  questionError = '';
  saving = false;
  severityLabel = severityLabel;
  private destroyed = new Subject<void>();

  constructor(private fb: FormBuilder,
              private questionService: QuestionsService,
              public authService: AuthService,
              private activatedRoute: ActivatedRoute,
              private router: Router) {
    this.questionId = this.activatedRoute.snapshot.paramMap.get('id');
    this.selectedDomain = this.activatedRoute.snapshot.queryParamMap.get('domain')?.toUpperCase() === 'MEDICAL' ? 'MEDICAL' : 'DENTAL';
    this.addQuestionsForm = this.fb.group({
      category: ['', Validators.required],
      questions: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.questionService.getCategoryDropdown({ domain: this.selectedDomain }).pipe(first(), takeUntil(this.destroyed)).subscribe(
      response => { this.categoryArray = Array.isArray(response) ? response : []; },
      error => this.showError(error)
    );
    this.loadSeverityTags();
    if (this.questionId) { this.loadQuestion(); } else { this.addQuestionRow(); }
    this.authService.clientId$.pipe(distinctUntilChanged(), skip(1), takeUntil(this.destroyed))
      .subscribe(() => this.router.navigate(['/questions'], { queryParams: { domain: this.selectedDomain } }));
  }

  ngOnDestroy(): void { this.destroyed.next(); this.destroyed.complete(); }
  get canEdit(): boolean { return this.authService.hasRole('client-edit') || this.authService.hasRole('audit-edit'); }
  get questions(): FormArray { return this.addQuestionsForm.get('questions') as FormArray; }
  isActiveSeverity(name: string): boolean { return this.severityOptions.some(tag => tag.name === name); }

  changeDomain(value: 'DENTAL' | 'MEDICAL'): void {
    if (this.questionId || value === this.selectedDomain || (value !== 'DENTAL' && value !== 'MEDICAL')) { return; }
    const apply = () => {
      this.selectedDomain = value;
      this.categoryArray = [];
      this.isCustomCategory = false;
      this.addQuestionsForm.get('category').reset();
      this.questionService.getCategoryDropdown({ domain: value }).pipe(first(), takeUntil(this.destroyed))
        .subscribe(response => { this.categoryArray = Array.isArray(response) ? response : []; },
          error => this.showError(error));
      this.router.navigate([], { relativeTo: this.activatedRoute, queryParams: { domain: value }, queryParamsHandling: 'merge' });
    };
    if (!this.addQuestionsForm.dirty) { apply(); return; }
    swal.fire({ title: 'Change question domain?', text: 'Unsaved changes will be discarded.',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Change domain' })
      .then(result => { if (result.isConfirmed) { apply(); } });
  }

  private setSeverityTags(tags: AuditSeverityTag[]): void {
    this.severityTags = tags;
    this.severityOptions = tags.filter(tag => tag.active);
    this.questions.controls.forEach(row => row.get('severity').updateValueAndValidity());
  }

  loadSeverityTags(): void {
    this.severityLoading = true;
    this.severityError = '';
    this.questionService.getSeverityTags().pipe(first(), takeUntil(this.destroyed)).subscribe(tags => {
      this.setSeverityTags(tags);
      this.severityLoading = false;
    }, error => {
      this.severityLoading = false;
      this.severityError = this.errorMessage(error);
    });
  }

  loadQuestion(): void {
    this.loadingQuestion = true;
    this.questionError = '';
    this.questionService.getQuestionById({ questionId: this.questionId, domain: this.selectedDomain }).pipe(first(), takeUntil(this.destroyed)).subscribe(data => {
      this.loadingQuestion = false;
      if (!data || !data.id) { this.questionError = 'Question not found. No changes can be saved.'; return; }
      this.addQuestionsForm.get('category').setValue(data.category || null);
      this.addQuestionsForm.get('category').disable();
      this.questions.clear();
      this.questions.push(this.questionFormFields(data));
    }, error => { this.loadingQuestion = false; this.questionError = this.errorMessage(error); });
  }

  private questionFormFields(data: any = {}): FormGroup {
    return this.fb.group({
      pseudoName: [data.pseudoName || null],
      question: [data.question || null, Validators.required],
      questionCode: [data.questionCode || null],
      severity: [data.severity || null, [Validators.required, control =>
        !control.value || this.isActiveSeverity(control.value) ? null : { inactiveSeverity: true }]]
    });
  }

  addQuestionRow(): void {
    if (!this.canEdit || this.saving) { return; }
    this.questions.markAllAsTouched();
    if (!this.questions.length || this.questions.valid) { this.questions.push(this.questionFormFields()); }
  }
  removeQuestion(index: number): void {
    if (this.canEdit && !this.saving && !this.questionId && this.questions.length > 1) { this.questions.removeAt(index); }
  }
  enableCustomCategory(): void {
    if (!this.canEdit || this.questionId) { return; }
    this.isCustomCategory = true;
    this.addQuestionsForm.get('category').setValue(null);
  }
  resetCategory(): void {
    if (!this.canEdit || this.questionId) { return; }
    this.isCustomCategory = false;
    this.addQuestionsForm.get('category').setValue(null);
  }

  submitForm(): void {
    this.addQuestionsForm.markAllAsTouched();
    if (!this.canEdit || this.saving || this.loadingQuestion || this.questionError || this.severityLoading ||
        this.severityError || !this.questions.length || this.addQuestionsForm.invalid) { return; }
    const raw = this.addQuestionsForm.getRawValue();
    const category = String(raw.category || '').trim();
    if (!category || raw.questions.some(question => !String(question.question || '').trim())) {
      this.showError(new Error('Category and question text cannot be blank.'));
      return;
    }
    const questions = raw.questions.map(question => ({
      pseudoName: question.pseudoName || null,
      question: question.question.trim(),
      questionCode: question.questionCode || null,
      severity: question.severity,
      benefitDomain: this.selectedDomain
    }));
    this.saving = true;
    const request = this.questionId
      ? this.questionService.updateQuestions({ category, ...questions[0] }, { questionId: this.questionId, domain: this.selectedDomain })
      : this.questionService.submitQuestions(questions, { category, domain: this.selectedDomain });
    request.pipe(first(), takeUntil(this.destroyed)).subscribe(() => {
      this.saving = false;
      swal.fire({ icon: 'success', text: this.questionId ? 'Question updated successfully.' : 'Questions added successfully.',
        showConfirmButton: false, timer: 2500 });
      this.router.navigate(['/questions'], { queryParams: { domain: this.selectedDomain } });
    }, error => { this.saving = false; this.showError(error); });
  }

  private errorMessage(error: any): string {
    return error?.error?.errors?.[0] || error?.error?.message || error?.message || 'Unable to complete the request. Please retry.';
  }
  private showError(error: any): void {
    swal.fire({ title: 'Error', icon: 'error', text: this.errorMessage(error) });
  }
}
